import { parseMarkup, ScriptLevel, type TextRun } from '@/domain/text';

/**
 * Laying a label out into lines.
 *
 * Pure given a measuring function, which is what keeps it testable: the measurement is the
 * one part that genuinely needs a canvas, so it arrives as a parameter and everything else —
 * wrapping, script offsets, line heights — is arithmetic that can be checked without a
 * browser.
 *
 * ## Measurement is cached
 *
 * A label is measured on every frame it is painted, and `measureText` is one of the more
 * expensive calls on a 2D context. The cache is keyed by text *and* size, because the same
 * word at two sizes is two different widths — a cache keyed on text alone is the classic way
 * to make subscripts come out the width of body text.
 */

export interface TextMeasurer {
  widthOf(text: string, fontPx: number): number;
}

export interface PositionedRun {
  readonly text: string;
  /** Offset from the label's anchor, in output units. */
  readonly x: number;
  readonly y: number;
  readonly fontPx: number;
}

export interface TextLayout {
  readonly lines: readonly (readonly PositionedRun[])[];
  readonly width: number;
  readonly height: number;
  readonly lineHeight: number;
}

export interface LayoutOptions {
  readonly fontPx: number;
  /**
   * Wrap width in output units. Omit to let the label grow — which is what a geometry label
   * usually wants, since it names a point rather than filling a column.
   */
  readonly wrapWidth?: number | undefined;
  readonly measure: TextMeasurer;
}

/** Script text is drawn at this fraction of the body size, the usual typographic ratio. */
export const SCRIPT_SCALE = 0.72;

/** How far a script sits off the baseline, as a fraction of the body size. */
export const SUPER_RISE = 0.42;
export const SUB_DROP = 0.18;

const LINE_SPACING = 1.28;

export const EMPTY_LAYOUT: TextLayout = Object.freeze({
  lines: [],
  width: 0,
  height: 0,
  lineHeight: 0,
});

/**
 * Lays out a label.
 *
 * Wrapping breaks between words, and only inside a **base** run. Breaking inside a subscript
 * would separate `A₁` from its `A`, which is not a line break but a corruption of the
 * notation. A single word longer than the wrap width overflows rather than being cut: a
 * broken-up label is harder to read than a wide one, and the caller framing an export
 * measures the result rather than assuming it.
 */
export function layoutText(content: string, options: LayoutOptions): TextLayout {
  const runs = parseMarkup(content);
  if (runs.length === 0) return EMPTY_LAYOUT;

  const lineHeight = options.fontPx * LINE_SPACING;
  const lines: PositionedRun[][] = [];

  let current: PositionedRun[] = [];
  let penX = 0;
  let widest = 0;

  const newline = (): void => {
    widest = Math.max(widest, penX);
    lines.push(current);
    current = [];
    penX = 0;
  };

  for (const run of runs) {
    const fontPx = run.level === ScriptLevel.Base ? options.fontPx : options.fontPx * SCRIPT_SCALE;
    const dy = offsetFor(run.level, options.fontPx);

    for (const piece of splitForWrapping(run, options.wrapWidth !== undefined)) {
      if (piece === '\n') {
        newline();
        continue;
      }

      const width = options.measure.widthOf(piece, fontPx);
      const wraps =
        options.wrapWidth !== undefined && current.length > 0 && penX + width > options.wrapWidth;

      if (wraps) {
        newline();
        // A leading space at the start of a wrapped line would indent it by a space width.
        const trimmed = piece.replace(/^ +/, '');
        const trimmedWidth = options.measure.widthOf(trimmed, fontPx);
        current.push({ text: trimmed, x: 0, y: dy, fontPx });
        penX = trimmedWidth;
        continue;
      }

      current.push({ text: piece, x: penX, y: dy, fontPx });
      penX += width;
    }
  }

  newline();

  // A trailing empty line from a final newline is real: the user pressed return.
  const height = lines.length * lineHeight;
  return { lines, width: widest, height, lineHeight };
}

function offsetFor(level: ScriptLevel, fontPx: number): number {
  switch (level) {
    case ScriptLevel.Base:
      return 0;
    case ScriptLevel.Super:
      return -fontPx * SUPER_RISE;
    case ScriptLevel.Sub:
      return fontPx * SUB_DROP;
  }
}

/**
 * Splits a run into the pieces wrapping may act between.
 *
 * Explicit newlines always split. Word boundaries split only when wrapping is on, so an
 * unwrapped label stays a single run and is measured once.
 */
function splitForWrapping(run: TextRun, wrapping: boolean): readonly string[] {
  const byLine = run.text.split('\n');
  const pieces: string[] = [];

  byLine.forEach((line, index) => {
    if (index > 0) pieces.push('\n');
    if (line === '') return;

    if (!wrapping || run.level !== ScriptLevel.Base) {
      // Never break inside a script: splitting `A₁` at the `₁` is not a line break, it is a
      // corruption of the notation.
      pieces.push(line);
      return;
    }

    // Keep the space with the word that follows it, so the break lands before the space and
    // the next line starts flush.
    for (const word of line.split(/(?= )/)) {
      if (word !== '') pieces.push(word);
    }
  });

  return pieces;
}

/**
 * A measurer over a 2D context, with a cache.
 *
 * The cache is unbounded on purpose. Its keys are the label strings in one document at the
 * sizes they are drawn, which is a set of tens, not thousands — an eviction policy would be
 * more code than the thing it guards. If a use ever arrives that generates unbounded label
 * text, this is the place to add one.
 */
export function createMeasurer(
  ctx: Pick<CanvasRenderingContext2D, 'measureText' | 'font'>,
  fontFamily: string
): TextMeasurer {
  const cache = new Map<string, number>();

  return {
    widthOf(text, fontPx) {
      const key = `${String(Math.round(fontPx * 100))}|${text}`;
      const cached = cache.get(key);
      if (cached !== undefined) return cached;

      ctx.font = `${String(fontPx)}px ${fontFamily}`;
      const width = ctx.measureText(text).width;
      cache.set(key, width);
      return width;
    },
  };
}

/**
 * A measurer that assumes a fixed character width.
 *
 * For the exporters that have no canvas — the PDF writer runs in a worker or a test as
 * readily as in a browser — and for tests of the layout itself. The ratio is Helvetica's
 * rough average, which is enough for framing and line breaking; a label whose exact width
 * matters is one being drawn, and a drawn label has a canvas.
 */
export function approximateMeasurer(ratio = 0.52): TextMeasurer {
  return {
    widthOf: (text, fontPx) => text.length * fontPx * ratio,
  };
}
